import { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import type { KickoffSlots, KickoffView } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { firstName } from '../lib/format';
import { Avatar, Banner } from '../ui';
import { useIntake } from './IntakeContext';
import { IntakeLayout } from './IntakeLayout';
import { KickoffPicker } from './KickoffPicker';
import { BOOKING_STEP, REVIEW_STEP } from './logic';
import { usePageHeading } from '../firm/usePageHeading';

export function Booking() {
  const { state, answers, setKickoff, submitted, reload } = useIntake();
  const navigate = useNavigate();
  const [host, setHost] = useState<KickoffSlots['host'] | null>(null);
  const onHost = useCallback((h: KickoffSlots['host']) => setHost(h), []);
  usePageHeading('Book your Discovery Session');
  if (!submitted) return <Navigate to={`/onboard?step=${REVIEW_STEP}`} replace />;
  if (state.kickoff && (state.kickoff.status === 'booked' || state.kickoff.status === 'contact_requested')) return <Navigate to="/onboard/done" replace />;

  const name = host?.name ?? state.lead.name;
  const title = host?.title ?? state.lead.title;
  return (
    <IntakeLayout step={BOOKING_STEP} maxReached={BOOKING_STEP}>
      <div className="flex flex-col gap-5">
        <Banner tone="success"><span className="font-semibold">Answers received. Thank you, {firstName(answers.contact?.name)}.</span></Banner>
        <div className="flex flex-col gap-2">
          <span className="eyebrow">Your first call</span>
          <h1 className="m-0 text-[26px] font-bold leading-[1.2] tracking-[-0.02em] sm:text-[30px]" tabIndex={-1} data-page-heading style={{ outline: "none" }}>Book your Discovery Session</h1>
          <p className="m-0 max-w-[680px] text-base leading-[1.55] text-ink-2">45 minutes by video. We go through your answers together, walk you through how moving to Upstream Portal works, from setup to go-live and the support after, and answer your questions.</p>
        </div>
        <div className="flex flex-col gap-6 rounded-card border border-line bg-white p-5 sm:p-6">
        <div className="flex items-center gap-3.5 border-b border-line-soft pb-5">
          <Avatar initials={state.lead.initials} tone="soft" />
          <div>
            <div className="font-semibold">{name}</div>
            <div className="text-sm text-muted">{title}, Upstream Portal</div>
          </div>
        </div>
        <KickoffPicker
          leadName={name}
          onHost={onHost}
          onConfirm={async (startsAt) => {
            const k = await api.post<KickoffView>('/api/intake/kickoff', { startsAt });
            setKickoff(k);
            navigate('/onboard/done');
            return k;
          }}
          onContactMe={async () => {
            try {
              const k = await api.post<KickoffView>('/api/intake/kickoff', { contactMe: true });
              setKickoff(k);
              navigate('/onboard/done');
            } catch (e) {
              // Booked meanwhile (another tab): reload so the page shows the booking instead of the picker.
              if (e instanceof ApiError && e.code === 'invalid_transition') await reload();
              throw e;
            }
          }}
        />
        </div>
      </div>
    </IntakeLayout>
  );
}
